import { useMemo } from 'react'
import { useSiteData } from '../context/DataContext.jsx'
import ErrorFallback from '../components/ErrorFallback.jsx'
import Skeleton from '../components/Skeleton.jsx'

function formatDateRange(start, end) {
  const startYear = new Date(start).getFullYear()
  const endYear = end ? new Date(end).getFullYear() : 'present'
  return `${startYear}–${endYear}`
}

function ResumePage() {
  const { data: siteData, loading, error, refetch } = useSiteData()
  const bio = siteData?.bio?.[0] || null
  const experiences = siteData?.experience || []
  const educations = siteData?.education || []
  const skills = siteData?.skills || []

  const groupedSkills = useMemo(() => {
    const map = {}
    for (const skill of skills) {
      if (!map[skill.category]) map[skill.category] = []
      map[skill.category].push(skill)
    }
    for (const cat of Object.keys(map)) {
      map[cat].sort((a, b) => b.comfort - a.comfort || a.name.localeCompare(b.name))
    }
    return map
  }, [skills])

  if (loading) {
    return (
      <div className="pg-layout">
        <div>
          <Skeleton className="h-4 w-28 mb-3" />
          <div className="flex justify-between items-baseline mb-10">
            <Skeleton className="h-14 w-80" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-5 w-96 mb-10" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="border-t border-soft-rule py-7">
              <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_200px] gap-4 sm:gap-8">
                <Skeleton className="h-4 w-20" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-5/6" />
                </div>
                <Skeleton className="h-3 w-24 justify-self-end" />
              </div>
            </div>
          ))}
        </div>
        <div></div>
      </div>
    )
  }

  if (error) return <ErrorFallback message="Failed to load resume" onRetry={refetch} />
  if (!bio) return <ErrorFallback message="No resume data found" />

  const CATEGORY_LABELS = {
    language: 'Languages',
    ai_ml: 'AI / ML',
    web: 'Web',
    data: 'Data',
    edtech: 'EdTech',
    infra: 'Infra',
    tooling: 'Tooling',
  }

  const CATEGORY_ORDER = ['language', 'ai_ml', 'web', 'data', 'edtech', 'infra', 'tooling']

  return (
    <div className="pg-layout">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.6px] text-soft-faint mb-3">
          Resume · one page
        </p>
        <div className="flex justify-between items-baseline mb-4">
          <h1 className="text-[36px] sm:text-[48px] md:text-[56px] font-normal leading-[1.02] tracking-[-1.8px]">
            Curriculum vitae.
          </h1>
          <button
            onClick={() => window.print()}
            className="text-sm text-soft-dim hover:text-soft-ink transition-colors cursor-pointer no-print"
          >
            Download PDF
          </button>
        </div>
        <p className="text-[18px] leading-[1.5] text-soft-dim max-w-[620px] mb-10">
          Built from live data. This page reflects what is in the database right now.
        </p>

        {/* Summary */}
        <div className="border-t border-soft-rule py-8">
          <div               className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-4 sm:gap-8">
            <span className="font-mono text-[12px] uppercase tracking-[0.6px] text-soft-faint">
              Summary
            </span>
            <div
              className="text-[16px] leading-[1.7] text-soft-ink"
              dangerouslySetInnerHTML={{ __html: bio.summary }}
            />
          </div>
        </div>

        {/* Experience */}
        <div className="border-t border-soft-rule pt-6 pb-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.6px] text-soft-faint mb-6">
            Experience
          </p>
          {experiences.map((exp, index) => (
            <div
              key={exp.id}
              className={`grid grid-cols-[120px_1fr_200px] gap-8 py-7 ${
                index > 0 ? 'border-t border-soft-rule' : ''
              }`}
            >
              <span className="font-mono text-[12px] text-soft-faint">
                {formatDateRange(exp.start_date, exp.end_date)}
              </span>
              <div>
                <h2 className="text-[20px] font-medium leading-[1.2] tracking-[-0.3px] text-soft-ink mb-1">
                  {exp.title}
                </h2>
                <p className="text-[15px] leading-[1.55] text-soft-dim mb-3">{exp.company}</p>
                {exp.formatted_bullets?.length > 0 && (
                  <ul className="space-y-1.5">
                    {exp.formatted_bullets.slice(0, 3).map((b) => (
                      <li key={b} className="text-[15px] leading-[1.55] text-soft-dim">
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="text-right">
                <p className="font-mono text-[12px] text-soft-faint mb-1">{exp.location}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Education */}
        <div className="border-t border-soft-rule pt-6 pb-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.6px] text-soft-faint mb-6">
            Education
          </p>
          {educations.map((edu, index) => (
            <div
              key={edu.id}
              className={`grid grid-cols-1 sm:grid-cols-[120px_1fr_240px] gap-4 sm:gap-8 py-7 ${
                index > 0 ? 'border-t border-soft-rule' : ''
              }`}
            >
              <span className="font-mono text-[12px] text-soft-faint">
                {edu.start_year}–{edu.end_year || 'present'}
              </span>
              <div>
                <h2 className="text-[20px] font-medium leading-[1.2] tracking-[-0.3px] text-soft-ink mb-1">
                  {edu.degree} in {edu.field}
                </h2>
                <p className="text-[15px] leading-[1.55] text-soft-dim">{edu.institution}</p>
              </div>
              <div className="text-right">
                {edu.grade && (
                  <p className="text-[28px] font-normal leading-[1] tracking-[-0.8px] text-soft-ink">
                    {edu.grade}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Skills */}
        <div className="border-t border-soft-rule pt-6 pb-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.6px] text-soft-faint mb-6">
            Skills
          </p>
          {CATEGORY_ORDER.map((cat, index) => {
            const catSkills = groupedSkills[cat] || []
            if (catSkills.length === 0) return null
            return (
              <div
                key={cat}
                className={`grid grid-cols-[120px_1fr] gap-8 py-5 ${
                  index > 0 ? 'border-t border-soft-rule' : ''
                }`}
              >
                <span className="font-mono text-[12px] uppercase tracking-[0.6px] text-soft-faint">
                  {CATEGORY_LABELS[cat]}
                </span>
                <p className="text-[16px] leading-[1.45] text-soft-ink">
                  {catSkills.map((s) => s.name).join(' · ')}
                </p>
              </div>
            )
          })}
        </div>
      </div>
      <div></div>
    </div>
  )
}

export default ResumePage
